import React from 'react'
import {
  InformationCircleIcon,
  UserIcon,
} from '@heroicons/react/24/outline'
import type { OrderDetails } from '@/shared/api'
import { useOrderCouriers } from '@/shared/hooks'
import { CourierLoadPanel } from './CourierLoadPanel'

const DASH = '—'

const formatText = (value: string | null | undefined): string =>
  value && value.trim() ? value : DASH

interface FieldProps {
  label: string
  children: React.ReactNode
}

const Field: React.FC<FieldProps> = ({ label, children }) => (
  <div>
    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
      {label}
    </dt>
    <dd className="mt-1 break-words text-sm text-gray-900">{children}</dd>
  </div>
)

export interface OrderCourierTabProps {
  order: OrderDetails
}

/**
 * Данные назначенного курьера. В карточке заказа нет externalUserId,
 * поэтому он берётся из справочника курьеров (тоже read-only запрос).
 */
const AssignedCourierFields: React.FC<{ order: OrderDetails }> = ({
  order,
}) => {
  const { data: couriers, isLoading } = useOrderCouriers(order.deliveryDate)
  const assigned = couriers?.find(
    (courier) => courier.userId === order.courierId
  )

  return (
    <dl className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Field label="Имя">
        {order.courierName
          ? order.courierName
          : `Курьер #${order.courierId}`}
      </Field>
      <Field label="Телефон">{formatText(order.courierPhone)}</Field>
      <Field label="Внешний ID">
        {isLoading ? 'Загрузка...' : formatText(assigned?.externalUserId)}
      </Field>
      <Field label="ID курьера">{String(order.courierId)}</Field>
    </dl>
  )
}

/**
 * Вкладка «Курьер»: назначенный курьер и его нагрузка на дату доставки.
 * Только чтение — назначить или сменить курьера здесь нельзя.
 */
export const OrderCourierTab: React.FC<OrderCourierTabProps> = ({ order }) => {
  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-2">
          <UserIcon className="h-4 w-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900">Курьер</h3>
        </div>

        {!order.courierId ? (
          <p className="mt-2 text-sm text-gray-500">Не назначен</p>
        ) : (
          <AssignedCourierFields order={order} />
        )}
      </section>

      {order.courierId && (
        <CourierLoadPanel
          courierId={order.courierId}
          deliveryDate={order.deliveryDate}
        />
      )}

      <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
        <InformationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
        <p className="text-sm text-gray-500">
          Назначение и смена курьера появятся в следующем срезе
        </p>
      </div>
    </div>
  )
}
